import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Coins, Wallet, ChevronRight, ChevronDown } from "lucide-react";

const gr = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const usd = (n) => (n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AltinCariCustomer() {
  const { name } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const toggleRow = (id) => setOpenRows((p) => ({ ...p, [id]: !p[id] }));

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/altin/by-customer/${encodeURIComponent(name)}`); setData(data); }
    catch (e) { toast.error("Kayıt yüklenemedi"); }
  }, [name]);

  useEffect(() => { load(); }, [load]);

  const pay = async (rec) => {
    const inp = window.prompt(`${rec.vade_tarihi} vadesi için ödenen USD tutarı (kalan ${usd(rec.kalan_usd)} $):`, rec.kalan_usd || "");
    if (inp == null) return;
    const tutar = Number(inp);
    if (!tutar || tutar <= 0) return toast.error("Geçerli USD tutarı girin");
    try {
      const { data: res } = await api.post(`/altin/${rec.id}/payment`, { tutar_usd: tutar });
      toast.success(`Ödeme alındı: ${usd(tutar)} $ → ${gr(res.odemeler[res.odemeler.length - 1].gram)} gr (kur ${res.odemeler[res.odemeler.length - 1].kur} $/gr)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Ödeme alınamadı"); }
  };

  const records = data?.records || [];
  const sozlesmeler = data?.sozlesmeler || [];

  return (
    <Layout>
      <button onClick={() => nav("/altin-cari")} className="inline-flex items-center gap-1 text-sm text-[#8C8377] hover:text-[#26231E] mb-3"><ArrowLeft className="w-4 h-4" /> Altın Cari</button>
      <div className="kicker text-[#B8933F] flex items-center gap-2 mb-1"><Coins className="w-4 h-4" /> ALTIN MÜŞTERİ</div>
      <h1 className="font-heading text-3xl font-bold text-[#26231E] mb-6">{decodeURIComponent(name)}</h1>

      {sozlesmeler.length === 0 ? (
        <div className="bg-white border border-[#E7DECC] rounded-sm p-10 text-center text-[#8C8377]">Kayıt yok.</div>
      ) : sozlesmeler.map((s) => {
        const rows = records.filter((r) => r.sozlesme_gid === s.sozlesme_gid);
        return (
          <div key={s.sozlesme_gid} className="mb-6" data-testid={`altin-sozlesme-block-${s.no}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <div className="font-heading font-semibold">{s.label} · {s.urun || "—"}</div>
              <div className="flex flex-wrap gap-2">
                <Mini label="Satış" v={`${gr(s.satis_gram)} gr`} />
                <Mini label="Peşinat" v={`${gr(s.pesinat_gram)} gr`} c="#7C5E9B" />
                <Mini label="Tahsilat" v={`${gr(s.tahsilat_gram)} gr`} c="#2F6E4E" />
                <Mini label="Kalan" v={`${gr(s.kalan_gram)} gr`} c={s.kalan_gram > 0 ? "#A63A3A" : "#2F6E4E"} />
              </div>
            </div>
            <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F3ECDD]"><tr className="border-b border-[#E7DECC]">
                  <th className="px-3 py-2.5 kicker text-left">Vade</th>
                  <th className="px-3 py-2.5 kicker text-right">Vade (USD)</th>
                  <th className="px-3 py-2.5 kicker text-right">Ödenen (USD)</th>
                  <th className="px-3 py-2.5 kicker text-right">Ödenen (gr)</th>
                  <th className="px-3 py-2.5 kicker text-right">Kalan (USD)</th>
                  <th className="px-3 py-2.5 kicker text-left">Durum</th>
                  <th className="px-3 py-2.5 kicker text-right">İşlem</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => {
                    const odm = r.odemeler || [];
                    const hasOdm = odm.length > 0;
                    const isOpen = !!openRows[r.id];
                    return (
                    <Fragment key={r.id}>
                    <tr className="border-b border-[#E7DECC]" data-testid={`altin-vade-row-${r.id}`}>
                      <td className="px-3 py-2 tabular-nums">
                        <button
                          onClick={() => hasOdm && toggleRow(r.id)}
                          disabled={!hasOdm}
                          data-testid={`altin-vade-toggle-${r.id}`}
                          className={`inline-flex items-center gap-1 ${hasOdm ? "cursor-pointer hover:text-[#B8933F]" : "cursor-default"}`}
                        >
                          {hasOdm ? (isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />) : <span className="w-3.5 inline-block" />}
                          {r.vade_tarihi}
                          {hasOdm && <span className="ml-1 text-[10px] text-[#8C8377]">({odm.length})</span>}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{usd(r.vade_usd)} $</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#2F6E4E]">{usd(r.odenen_usd)} $</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#B8933F]">{gr(r.odenen_gram)} gr</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.kalan_usd > 0 ? "text-[#A63A3A]" : "text-[#2F6E4E]"}`}>{usd(r.kalan_usd)} $</td>
                      <td className="px-3 py-2"><span className="text-xs font-semibold" style={{ color: r.durum === "Vadesi Geçti" ? "#A63A3A" : r.durum === "Ödendi" ? "#2F6E4E" : "#8C8377" }}>{r.durum}</span></td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => pay(r)} disabled={r.kalan_usd <= 0} data-testid={`altin-pay-${r.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-[#B8933F] border border-[#E7DECC] rounded-sm text-xs font-medium hover:bg-[#F3ECDD] disabled:opacity-40">
                          <Wallet className="w-3.5 h-3.5" /> USD Ödeme
                        </button>
                      </td>
                    </tr>
                    {isOpen && hasOdm && (
                      <tr data-testid={`altin-odeme-detay-${r.id}`}>
                        <td colSpan={7} className="px-3 py-0 bg-[#FBF7EE] border-b border-[#E7DECC]">
                          <div className="py-2">
                            <div className="kicker text-[#B8933F] mb-1.5">ÖDEME DÖKÜMÜ</div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-[#8C8377]">
                                  <th className="px-2 py-1 text-left font-medium">Tarih</th>
                                  <th className="px-2 py-1 text-right font-medium">Ödenen (USD)</th>
                                  <th className="px-2 py-1 text-right font-medium">Kur ($/gr)</th>
                                  <th className="px-2 py-1 text-right font-medium">Alınan (gr)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {odm.map((o, oi) => (
                                  <tr key={oi} className="border-t border-[#E7DECC]/60" data-testid={`altin-odeme-satir-${r.id}-${oi}`}>
                                    <td className="px-2 py-1 tabular-nums">{o.tarih}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-[#2F6E4E]">{usd(o.usd)} $</td>
                                    <td className="px-2 py-1 text-right tabular-nums">{usd(o.kur)}</td>
                                    <td className="px-2 py-1 text-right tabular-nums text-[#B8933F]">{gr(o.gram)} gr</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </Layout>
  );
}

function Mini({ label, v, c }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm px-3 py-1.5 text-center min-w-[96px]">
      <div className="kicker text-[10px]">{label}</div>
      <div className="font-heading font-bold text-sm tabular-nums" style={c ? { color: c } : undefined}>{v}</div>
    </div>
  );
}
